import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Auth } from '@lighty';
import { AuthService } from '@services';
import { environment } from '@env/environment';
import { timeout } from 'rxjs/operators';

/**
 * Entrada al mini-program de Toka: obtiene el permiso del usuario, canjea el
 * código contra nuestro backend e inicia sesión.
 *
 * Los pings de diagnóstico a /toka/ping se conservan a propósito. Son la única
 * observabilidad que tenemos dentro del WebView de Toka, donde ya hemos perdido
 * peticiones sin rastro; no afectan al flujo y no se ven en pantalla.
 */
@Component({
    selector: 'msc-toka',
    templateUrl: './toka.component.html',
    styleUrls: ['./toka.component.scss'],
})
export class TokaComponent implements OnInit {

    public status: 'loading' | 'error' = 'loading';
    public errorMessage = '';
    private bridgeInitStarted = false;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.ping('page_loaded');
        this.waitForBridgeThenInit();
    }

    /**
     * Checkpoint fire-and-forget hacia el backend (GET simple, sin preflight
     * CORS). Si el WebView se cuelga justo después, el ping ya salió y queda
     * registrado. Nunca debe romper el flujo real.
     */
    private ping(step: string, detail?: string): void {
        try {
            const params = new URLSearchParams({ step });
            if (detail) {
                params.set('detail', detail.substring(0, 200));
            }
            this.http.get(`${environment.envVar.API_URL}/toka/ping?${params.toString()}`).subscribe();
        } catch {
            // El ping es puramente informativo.
        }
    }

    /**
     * Según el FAQ oficial de Alipay Mini Program: llamar una JSAPI antes de que
     * el JS bridge nativo termine de inyectarse produce el mismo "JSAPI call
     * denied" que una JSAPI inexistente o sin permisos — hay que esperar el
     * evento AlipayJSBridgeReady antes de invocar nada.
     */
    private waitForBridgeThenInit(): void {
        try {
            if ((window as any).AlipayJSBridge) {
                this.ping('bridge:success');
                this.initTokaAuth();
                return;
            }

            document.addEventListener('AlipayJSBridgeReady', () => {
                this.ping('bridge:success');
                this.initTokaAuth();
            }, false);

            setTimeout(() => {
                if (!this.bridgeInitStarted) {
                    this.ping('bridge:error', 'nunca apareció AlipayJSBridge');
                    this.showError('Esta página solo funciona dentro de la aplicación Toka.');
                }
            }, 5000);
        } catch (e: any) {
            this.showError('Excepción al iniciar: ' + (e?.message || String(e)));
        }
    }

    /**
     * Guía SOP oficial de Tokapay: AlipayJSBridge.call('getUserXxxAuthCode',
     * { scopes: [...], usage: '...' }, callback) — sin prefijo "my.".
     */
    private initTokaAuth(): void {
        try {
            if (this.bridgeInitStarted) {
                return;
            }
            this.bridgeInitStarted = true;

            this.ping('calling_contact_jsapi');

            // El teléfono va en la MISMA llamada que el email: ambos pertenecen a
            // la categoría "Contact Information", así que no añade ninguna llamada
            // nueva al bridge. Hace falta para Agreement Pay, donde getSignSms lo
            // contrasta contra los registros de Toka.
            const called = this.callTokaJsapi(
                'getUserContactInformationAuthCode',
                {
                    scopes: ['PLAINTEXT_EMAIL_ADDRESS', 'PLAINTEXT_MOBILE_PHONE'],
                    usage: 'Inicio de sesión y suscripción en MySkillCamp',
                },
                (contactData: any) => this.onContactAuthCodeReceived(contactData)
            );

            if (!called) {
                this.showError('Esta página solo funciona dentro de la aplicación Toka.');
            }
        } catch (e: any) {
            this.showError('Excepción al pedir el permiso: ' + (e?.message || String(e)));
        }
    }

    /**
     * Envuelve tanto la invocación al bridge como el callback en try/catch:
     * cualquier excepción silenciosa (que de otro modo colgaría la pantalla
     * sin rastro) se muestra como error visible.
     */
    private callTokaJsapi(methodName: string, params: any, callback: (data: any) => void): boolean {
        const bridge = (window as any).AlipayJSBridge;

        if (!bridge) {
            return false;
        }

        try {
            bridge.call(methodName, params, (data: any) => {
                try {
                    callback(data);
                } catch (e: any) {
                    this.showError(`Excepción procesando respuesta de ${methodName}: ` + (e?.message || String(e)));
                }
            });
            return true;
        } catch (e: any) {
            this.showError(`Excepción llamando ${methodName}: ` + (e?.message || String(e)));
            return true;
        }
    }

    private onContactAuthCodeReceived(contactData: any): void {
        // El authCode viene en `result`, NO en `authCode` (confirmado en vivo).
        if (!contactData || contactData.resultCode !== 10000 || !contactData.result) {
            const detail = contactData?.resultMsg ? ` (${contactData.resultMsg})` : '';
            this.ping('contact:error', contactData?.resultMsg);
            this.showError('No se pudo obtener el código de autorización de Toka.' + detail);
            return;
        }

        this.ping('contact:success');

        // Los apellidos no se piden aquí: volver a llamar al bridge durante el
        // login reabriría el congelamiento del WebView. Se obtienen en la
        // pantalla de firma, donde un fallo no impide entrar.
        // Se difiere a un macrotask nuevo por el mismo motivo.
        setTimeout(() => this.submitEntrada(contactData.result), 0);
    }

    private submitEntrada(authCode: string): void {
        // appId NO viene en la respuesta de la JSAPI — el backend usa su propio
        // config('toka.app_id').
        const url = `${environment.envVar.API_URL}/toka/entrada`;
        this.ping('about_to_post_entrada');

        this.http.post(url, { authCode }).pipe(timeout(15000)).subscribe({
            next: (response: any) => {
                this.ping('backend:success');

                // La ruta pasa por el middleware snakeCaseToCamelCase de api-2,
                // así que los tokens llegan como accessToken/refreshToken. Se
                // aceptan ambas formas para no depender de ese middleware.
                const accessToken = response?.accessToken ?? response?.access_token;
                const refreshToken = response?.refreshToken ?? response?.refresh_token;

                // Sin esta guarda, unos tokens vacíos generaban cookies vacías y
                // una redirección silenciosa a la pantalla de login.
                if (!accessToken || !refreshToken) {
                    this.ping('session:error', 'respuesta sin tokens');
                    this.showError('El backend respondió sin tokens de sesión.');
                    return;
                }

                this.authService.createCookies(new Auth({
                    access_token: accessToken,
                    refresh_token: refreshToken,
                }));

                this.ping('session:success');

                // El backend decide el destino: sin suscripción activa, a firmarla.
                window.location.href = response?.destino ?? '/';
            },
            error: (err: any) => {
                if (err?.name === 'TimeoutError') {
                    this.ping('backend:error', 'timeout del cliente');
                    this.showError('El backend no respondió a tiempo. Inténtalo de nuevo.');
                    return;
                }

                const message = err?.error?.message || err?.message || 'Error al iniciar sesión con Toka.';
                this.ping('backend:error', `HTTP ${err?.status ?? '?'}`);
                this.showError(message);
            },
        });
    }

    private showError(message: string): void {
        this.status = 'error';
        this.errorMessage = message;
    }
}
